<template>
  <div v-if="pageData">
    <HeroDossier v-bind="pageData" />
    <Grid variant="detail" class="dossiers-grid">
      <PageContent :content="state.content" m2a-field="dossiers_content" :start-index="1" />

      <TeaserGroupLoadMoreGrid
        title="Artikelen in dit dossier"
        loading="lazy"
        :filter="relatedFilter"
        :teaser-count="12"
        :cache-key="baseCacheKey"
        :narrow="true"
        class="is--full-detail-width-medium is--full-detail-width-large"
      />
    </Grid>
  </div>
</template>

<script setup lang="ts">
import usePageStore from '~/stores/page';
import useRelatedFilter from '~/composables/useRelatedFilter';
import useCollectionInstance from '~/composables/useCollectionInstance';
import type { ICmsDossiers, ICmsDossiersContent } from '~~/types/_cms-types';
import type { IDossiersContent } from '~~/types/local_types';

const pageStore = usePageStore();
const { slug } = useRoute().params;
const baseCacheKey = `dossiers-${slug}`;

const pageData = (await useCollectionInstance('dossiers')) as ICmsDossiers;
if (!pageData) {
  throw createError({ statusCode: 404, statusMessage: 'Dossier niet gevonden' });
}

usePageSeo({
  pageTitle: pageData.title,
  pageDescription: pageData.description,
  pageImage: pageData.image,
  section: 'Dossiers',
});

const relatedFilter = useRelatedFilter({
  field: 'dossiers',
  id: pageData.id,
});

pageStore.setCurrentPage(undefined);

const transformArray = (items: ICmsDossiersContent[]): IDossiersContent[] => {
  const result = [];
  let contentLabeledTeasers = [];

  for (const [index, item] of items.entries()) {
    if (item.collection === 'content_labeled_teasers') {
      contentLabeledTeasers.push(item.item);
    } else {
      if (contentLabeledTeasers.length > 0) {
        result.push({
          collection: 'content_labeled_teasers',
          id: index,
          staticData: [...contentLabeledTeasers],
        });
        contentLabeledTeasers = [];
      }
      result.push(item);
    }
  }

  if (contentLabeledTeasers.length > 0) {
    result.push({
      collection: 'content_labeled_teasers',
      id: items.length,
      staticData: contentLabeledTeasers,
    });
  }

  return result;
};

const state = reactive({
  content: transformArray(pageData.content || []),
});

if (import.meta.client) {
  sendAnalytics({ prefix: 'dossiers', slug: pageData.slug });
}
</script>

<style scoped lang="postcss">
.dossiers-grid {
  @media (--md-viewport) {
    > * {
      padding-left: var(--grid-has-padding-small) !important;
      padding-right: var(--grid-has-padding-small) !important;
      grid-column: 2 / 5 !important;
    }
  }

  @media (--lg-viewport) {
    > * {
      padding-left: var(--grid-has-padding-medium) !important;
      padding-right: var(--grid-has-padding-medium) !important;
      grid-column: 3 / 6 !important;
    }
  }
}
</style>
